import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, AuthState, LoginCredentials } from '../types';
import { SAMPLE_USERS } from '../constants/sampleUsers';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<boolean>;
  logout: () => void;
  hasPermission: (resource: string, action: string) => boolean;
  canAccessClient: (clientId: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true
  });

  useEffect(() => {
    // Check for existing session on app load
    const checkExistingSession = () => {
      const savedUser = localStorage.getItem('dealercentives_user');
      if (savedUser) {
        try {
          const userData = JSON.parse(savedUser);
          
          // Convert date strings back to Date objects
          const user = {
            ...userData,
            createdAt: userData.createdAt ? new Date(userData.createdAt) : new Date(),
            lastLogin: userData.lastLogin ? new Date(userData.lastLogin) : new Date()
          };
          
          setAuthState({
            user,
            isAuthenticated: true,
            isLoading: false
          });
        } catch (error) {
          localStorage.removeItem('dealercentives_user');
          setAuthState({
            user: null,
            isAuthenticated: false,
            isLoading: false
          });
        }
      } else {
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false
        });
      }
    };

    checkExistingSession();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    setAuthState(prev => ({ ...prev, isLoading: true }));

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Find user in sample data
    const user = SAMPLE_USERS.find(u => 
      u.email.toLowerCase() === credentials.email.toLowerCase()
    );

    if (user) {
      // In a real app, you'd verify the password here
      // For demo purposes, we'll accept any password for existing users
      
      const updatedUser = {
        ...user,
        lastLogin: new Date()
      };

      localStorage.setItem('dealercentives_user', JSON.stringify(updatedUser));
      
      setAuthState({
        user: updatedUser,
        isAuthenticated: true,
        isLoading: false
      });

      return true;
    } else {
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false
      });
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('dealercentives_user');
    setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false
    });
  };

  const hasPermission = (resource: string, action: string): boolean => {
    if (!authState.user) return false;

    // Super admin has all permissions
    if (authState.user.role === 'super_admin') return true;

    // Check user's specific permissions
    const permission = authState.user.permissions.find(p => p.resource === resource);
    return permission ? permission.actions.includes(action as any) : false;
  };

  const canAccessClient = (clientId: string): boolean => {
    if (!authState.user) return false;

    // Super admin and admin can access all clients
    if (authState.user.role === 'super_admin' || authState.user.role === 'admin') {
      return true;
    }

    // Client users can only access their assigned client
    if (authState.user.role === 'client' || authState.user.role === 'viewer') {
      return authState.user.clientId === clientId;
    }

    return false;
  };

  return (
    <AuthContext.Provider value={{
      ...authState,
      login,
      logout,
      hasPermission,
      canAccessClient
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};